import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { API } from '../App';
import Header from '../components/layout/Header';

export default function SharedTripChat() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [chat, setChat] = useState(null);
  const [substance, setSubstance] = useState(null);
  const [persona, setPersona] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    API.get(`/api/trip-chats/shared/${token}`)
      .then(async (res) => {
        setChat(res.data);
        const [substRes, persRes] = await Promise.all([
          API.get(`/api/altered-states/${res.data.substance_slug}`).catch(() => null),
          API.get(`/api/personas/${res.data.persona_slug}`).catch(() => null),
        ]);
        if (substRes) setSubstance(substRes.data);
        if (persRes) setPersona(persRes.data);
      })
      .catch(() => setError('Shared trip chat not found'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Header />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#A855F7] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !chat) {
    return (
      <div className="min-h-screen bg-[#0F0F0F]">
        <Header />
        <div className="pt-20 flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <p className="text-[#EF4444] mb-4">{error || 'Not found'}</p>
            <button onClick={() => navigate('/')} className="px-4 py-2 bg-[#A855F7] text-white text-sm">Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F0F0F] flex flex-col">
      <Header />
      <div className="pt-14 flex-1 flex flex-col lg:flex-row">
        {/* Sidebar */}
        <div className="lg:w-72 shrink-0 border-b lg:border-b-0 lg:border-r border-[#2A2A2A] p-4 lg:p-6">
          <button onClick={() => navigate('/')} className="flex items-center gap-1 text-xs text-[#A0A0A0] hover:text-[#F5F5F5] transition-colors mb-4">
            <ArrowLeft className="w-3 h-3" /> Home
          </button>

          <div className="mb-4 p-3 bg-[#A855F7]/10 border border-[#A855F7]/30">
            <span className="text-[9px] font-mono text-[#A855F7] block mb-1">SHARED TRIP</span>
            <p className="text-xs text-[#F5F5F5]">{chat.title}</p>
          </div>

          {substance && (
            <div className="mb-4">
              <span className="text-2xl block mb-1">{substance.emoji}</span>
              <h3 className="text-sm font-bold font-['Barlow_Condensed'] text-[#A855F7]">{substance.name}</h3>
            </div>
          )}
          {persona && (
            <div className="p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
              <span className="text-sm font-medium font-['Barlow_Condensed']" style={{ color: persona.color || '#F5F5F5' }}>
                {persona.name}
              </span>
            </div>
          )}
          {chat.topic && (
            <div className="mt-4 p-3 bg-[#1A1A1A] border border-[#2A2A2A]">
              <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">TOPIC</span>
              <p className="text-xs text-[#F5F5F5]">{chat.topic}</p>
            </div>
          )}
        </div>

        {/* Messages (read-only) */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-3">
          {(chat.messages || []).map((msg, i) => {
            const isUser = msg.role === 'user';
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[80%] p-3 ${
                  isUser
                    ? 'bg-[#FF4D00]/10 border border-[#FF4D00]/30 ml-auto'
                    : 'bg-[#1A1A1A] border border-[#2A2A2A]'
                }`}>
                  {!isUser && persona && (
                    <span className="text-[10px] font-mono text-[#A855F7] block mb-1">
                      {persona.name} {substance?.emoji}
                    </span>
                  )}
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
